import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Receipt, CheckCircle2, AlertTriangle, Clock, XCircle,
  TrendingUp, Search, RefreshCw, Plus, Trash2, DollarSign, Equal,
} from "lucide-react";

const ESTADOS = [
  { value: "todos", label: "Todos", icon: Receipt, color: "#5c7368" },
  { value: "pendiente", label: "Pendientes", icon: Clock, color: "#a55700" },
  { value: "vencida_pendiente", label: "Vencidas >30d", icon: AlertTriangle, color: "#b53a25" },
  { value: "recibida", label: "Recibidas", icon: CheckCircle2, color: "#205c45" },
  { value: "diferencia", label: "Con diferencia", icon: Equal, color: "#7a4000" },
  { value: "no_aplica", label: "No aplican", icon: XCircle, color: "#8ba397" },
];

const ESTADO_BADGE = {
  pendiente: { cls: "bg-[#fef7ec] text-[#a55700] border-[#f5c477]", txt: "⏳ Pendiente" },
  vencida_pendiente: { cls: "bg-[#fce8e3] text-[#b53a25] border-[#f5a594]", txt: "⚠️ >30 días" },
  recibida: { cls: "bg-[#e9f2ee] text-[#205c45] border-[#9ec4b1]", txt: "✅ Recibida" },
  diferencia: { cls: "bg-[#fff3e0] text-[#7a4000] border-[#dfb88a]", txt: "🟡 Diferencia" },
  no_aplica: { cls: "bg-[#f5f8f6] text-[#8ba397] border-[#d1dbd6]", txt: "🚫 No aplica" },
};

const fmt = (n) => (n != null ? `$${(+n).toFixed(2)}` : "—");

export default function RetencionesRecibidasControl() {
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("todos");
  const [desde, setDesde] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [hasta, setHasta] = useState(() => new Date().toISOString().slice(0, 10));
  const [registrandoRow, setRegistrandoRow] = useState(null); // row in modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      if (estado !== "todos") params.set("estado", estado);
      const [{ data: rowsData }, { data: kpisData }] = await Promise.all([
        api.get(`/retenciones-recibidas/control?${params}`),
        api.get(`/retenciones-recibidas/control/kpis?${new URLSearchParams({ desde, hasta })}`),
      ]);
      setRows(rowsData || []);
      setKpis(kpisData || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, estado]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const s = search.trim().toLowerCase();
    return rows.filter(r =>
      (r.numero || "").toLowerCase().includes(s) ||
      (r.cliente_nombre || "").toLowerCase().includes(s) ||
      (r.cliente_numero_identificacion || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  return (
    <div className="space-y-4" data-testid="retenciones-control">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={DollarSign}
          label="Retención esperada"
          value={fmt(kpis.total_esperada)}
          subtitle={`${kpis.total_aplica || 0} facturas aplican`}
          tone="info"
        />
        <KpiCard
          icon={CheckCircle2}
          label="Retención recibida"
          value={fmt(kpis.total_recibida)}
          subtitle={`${(kpis.cumplimiento_pct ?? 0).toFixed(1)}% cumplimiento`}
          tone="success"
        />
        <KpiCard
          icon={Clock}
          label="Pendientes"
          value={kpis.n_pendientes || 0}
          subtitle={`${kpis.n_vencidas || 0} vencidas >30 días`}
          tone="warning"
        />
        <KpiCard
          icon={TrendingUp}
          label="Diferencia neta"
          value={fmt(kpis.total_diferencia)}
          subtitle={kpis.n_diferencias ? `${kpis.n_diferencias} facturas con diferencia` : "Sin diferencias"}
          tone={Math.abs(+kpis.total_diferencia || 0) > 1 ? "danger" : "default"}
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-2 bg-white p-3 rounded border border-[#d1dbd6]">
        <div>
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="h-9 w-40" data-testid="retencion-filtro-desde" />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="h-9 w-40" data-testid="retencion-filtro-hasta" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Buscar</Label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-3 text-[#8ba397]" />
            <Input
              placeholder="N° factura, cliente, RUC..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-9 pl-7"
              data-testid="retencion-search"
            />
          </div>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="h-9" disabled={loading} data-testid="retencion-refresh">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </div>

      {/* Pestañas por estado */}
      <div className="flex flex-wrap gap-2">
        {ESTADOS.map(e => {
          const Icon = e.icon;
          const active = estado === e.value;
          const count =
            e.value === "todos" ? kpis.total_facturas :
            e.value === "pendiente" ? (kpis.n_pendientes || 0) - (kpis.n_vencidas || 0) :
            e.value === "vencida_pendiente" ? kpis.n_vencidas :
            e.value === "recibida" ? kpis.n_recibidas :
            e.value === "diferencia" ? kpis.n_diferencias :
            e.value === "no_aplica" ? kpis.total_no_aplica : 0;
          return (
            <button
              key={e.value}
              onClick={() => setEstado(e.value)}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 border transition-colors ${
                active ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#15241d] border-[#d1dbd6] hover:bg-[#e9f2ee]"
              }`}
              data-testid={`retencion-estado-${e.value}`}
            >
              <Icon size={14} style={{ color: active ? "white" : e.color }} />
              {e.label}
              <span className={`text-[10px] font-mono ${active ? "text-white/80" : "text-[#5c7368]"}`}>({count || 0})</span>
            </button>
          );
        })}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded border border-[#d1dbd6] overflow-x-auto">
        <table className="erp-table w-full text-sm">
          <thead className="bg-[#f5f8f6]">
            <tr>
              <th className="text-left p-2">Factura</th>
              <th className="text-left p-2">Fecha</th>
              <th className="text-left p-2">Cliente</th>
              <th className="text-right p-2">Subtotal</th>
              <th className="text-right p-2">IVA</th>
              <th className="text-right p-2">Esp. Renta</th>
              <th className="text-right p-2">Esp. IVA</th>
              <th className="text-right p-2 font-bold">Esperada</th>
              <th className="text-right p-2 font-bold">Recibida</th>
              <th className="text-right p-2">Δ Dif.</th>
              <th className="text-left p-2">Estado</th>
              <th className="text-right p-2 w-24">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const badge = ESTADO_BADGE[r.estado_retencion] || ESTADO_BADGE.pendiente;
              return (
                <tr key={r.factura_id} className="border-b border-[#eee] hover:bg-[#fafafa]">
                  <td className="p-2 font-mono text-xs">{r.numero}</td>
                  <td className="p-2 text-xs">{r.fecha}</td>
                  <td className="p-2">
                    <div className="font-medium truncate max-w-[180px]" title={r.cliente_nombre}>{r.cliente_nombre || "—"}</div>
                    <div className="text-[10px] text-[#5c7368]">{r.cliente_tipo_identificacion} {r.cliente_numero_identificacion}</div>
                  </td>
                  <td className="p-2 text-right tabular-nums">{fmt(r.subtotal)}</td>
                  <td className="p-2 text-right tabular-nums text-[#5c7368]">{fmt(r.iva)}</td>
                  <td className="p-2 text-right tabular-nums text-[#5c7368]">{r.aplica ? fmt(r.renta_esperada) : "—"}</td>
                  <td className="p-2 text-right tabular-nums text-[#5c7368]">{r.aplica ? fmt(r.iva_esperada) : "—"}</td>
                  <td className="p-2 text-right tabular-nums font-semibold text-[#205c45]">{r.aplica ? fmt(r.total_esperada) : "—"}</td>
                  <td className="p-2 text-right tabular-nums font-semibold">{r.recibida_total > 0 ? fmt(r.recibida_total) : <span className="text-[#8ba397]">—</span>}</td>
                  <td className={`p-2 text-right tabular-nums ${Math.abs(r.diferencia) > 0.05 ? "text-[#b53a25]" : "text-[#205c45]"}`}>
                    {r.aplica ? (Math.abs(r.diferencia) > 0.05 ? fmt(r.diferencia) : "✓") : "—"}
                  </td>
                  <td className="p-2"><Badge className={`border ${badge.cls}`}>{badge.txt}</Badge></td>
                  <td className="p-2 text-right">
                    {r.aplica && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRegistrandoRow(r)}
                        className="h-7 text-xs border-[#205c45] text-[#205c45] hover:bg-[#e9f2ee]"
                        data-testid={`retencion-registrar-${r.factura_id}`}
                      >
                        <Plus size={12} className="mr-1" /> Registrar
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="text-center py-10 text-[#8ba397]">
                {loading ? "Cargando..." : "Sin facturas en este rango / estado."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {registrandoRow && (
        <RegistrarRetencionDialog
          row={registrandoRow}
          onClose={() => setRegistrandoRow(null)}
          onSaved={() => { setRegistrandoRow(null); load(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle, tone = "default" }) {
  const tones = {
    default: "bg-white border-[#d1dbd6] text-[#15241d]",
    info: "bg-[#e9f2ee] border-[#9ec4b1] text-[#205c45]",
    success: "bg-[#e9f2ee] border-[#9ec4b1] text-[#205c45]",
    warning: "bg-[#fef7ec] border-[#f5c477] text-[#7a4000]",
    danger: "bg-[#fce8e3] border-[#f5a594] text-[#b53a25]",
  };
  return (
    <div className={`p-4 rounded-lg border-2 ${tones[tone]}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
          {subtitle && <div className="text-[11px] mt-1 opacity-80">{subtitle}</div>}
        </div>
        <Icon size={20} className="opacity-50 flex-shrink-0" />
      </div>
    </div>
  );
}

function RegistrarRetencionDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    numero_comprobante: "",
    clave_acceso_sri: "",
    numero_autorizacion_sri: "",
    fecha: new Date().toISOString().slice(0, 10),
    valor_retenido_ir: row.renta_esperada,
    porcentaje_ir: ((row.renta_pct || 0) * 100).toFixed(2),
    base_ir: row.subtotal,
    valor_retenido_iva: row.iva_esperada,
    porcentaje_iva: ((row.iva_pct || 0) * 100).toFixed(0),
    base_iva: row.iva,
    observacion: "",
  });
  const [saving, setSaving] = useState(false);

  const totalRet = (parseFloat(form.valor_retenido_ir) || 0) + (parseFloat(form.valor_retenido_iva) || 0);

  const save = async () => {
    if (!form.numero_comprobante.trim()) {
      toast.error("Ingresa el número de comprobante de retención");
      return;
    }
    if (totalRet <= 0) {
      toast.error("Debe haber al menos un valor de retención > 0");
      return;
    }
    setSaving(true);
    try {
      await api.post("/retenciones-recibidas", {
        cliente_id: row.cliente_id,
        factura_id: row.factura_id,
        numero_comprobante: form.numero_comprobante.trim(),
        clave_acceso_sri: form.clave_acceso_sri.trim() || null,
        numero_autorizacion_sri: form.numero_autorizacion_sri.trim() || null,
        fecha: form.fecha,
        base_ir: parseFloat(form.base_ir) || 0,
        porcentaje_ir: parseFloat(form.porcentaje_ir) || 0,
        valor_retenido_ir: parseFloat(form.valor_retenido_ir) || 0,
        base_iva: parseFloat(form.base_iva) || 0,
        porcentaje_iva: parseFloat(form.porcentaje_iva) || 0,
        valor_retenido_iva: parseFloat(form.valor_retenido_iva) || 0,
        observacion: form.observacion.trim(),
      });
      toast.success(`✓ Retención registrada por ${fmt(totalRet)}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="registrar-retencion-dialog">
        <DialogHeader>
          <DialogTitle>Registrar retención recibida — Factura {row.numero}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-[#f5f8f6] rounded p-3 text-sm">
            <div className="flex justify-between"><span className="text-[#5c7368]">Cliente:</span> <strong>{row.cliente_nombre}</strong></div>
            <div className="flex justify-between"><span className="text-[#5c7368]">Subtotal:</span> <span className="tabular-nums">{fmt(row.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#5c7368]">IVA:</span> <span className="tabular-nums">{fmt(row.iva)}</span></div>
            <div className="flex justify-between border-t pt-1 mt-1"><span className="text-[#5c7368]">Esperada total:</span> <strong className="text-[#205c45] tabular-nums">{fmt(row.total_esperada)}</strong></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>N° Comprobante *</Label>
              <Input
                value={form.numero_comprobante}
                onChange={e => setForm({ ...form, numero_comprobante: e.target.value })}
                placeholder="001-001-000000123"
                data-testid="ret-numero-comprobante"
              />
            </div>
            <div>
              <Label>Fecha emisión *</Label>
              <Input
                type="date"
                value={form.fecha}
                onChange={e => setForm({ ...form, fecha: e.target.value })}
                data-testid="ret-fecha"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>N° Autorización SRI (49 díg.)</Label>
              <Input
                value={form.clave_acceso_sri}
                onChange={e => setForm({ ...form, clave_acceso_sri: e.target.value })}
                placeholder="Opcional"
                className="font-mono text-xs"
              />
            </div>
            <div>
              <Label>N° Autorización corta</Label>
              <Input
                value={form.numero_autorizacion_sri}
                onChange={e => setForm({ ...form, numero_autorizacion_sri: e.target.value })}
                placeholder="Opcional"
              />
            </div>
          </div>

          <div className="border rounded p-3 bg-white">
            <div className="text-xs font-semibold text-[#205c45] uppercase mb-2">Retención Renta (IR)</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Base imponible</Label>
                <Input type="number" step="0.01" value={form.base_ir} onChange={e => setForm({ ...form, base_ir: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">%</Label>
                <Input type="number" step="0.01" value={form.porcentaje_ir} onChange={e => setForm({ ...form, porcentaje_ir: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Valor retenido</Label>
                <Input type="number" step="0.01" value={form.valor_retenido_ir} onChange={e => setForm({ ...form, valor_retenido_ir: e.target.value })} data-testid="ret-valor-ir" className="font-semibold" />
              </div>
            </div>
          </div>

          <div className="border rounded p-3 bg-white">
            <div className="text-xs font-semibold text-[#205c45] uppercase mb-2">Retención IVA</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Base (IVA factura)</Label>
                <Input type="number" step="0.01" value={form.base_iva} onChange={e => setForm({ ...form, base_iva: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">%</Label>
                <Input type="number" step="0.01" value={form.porcentaje_iva} onChange={e => setForm({ ...form, porcentaje_iva: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Valor retenido</Label>
                <Input type="number" step="0.01" value={form.valor_retenido_iva} onChange={e => setForm({ ...form, valor_retenido_iva: e.target.value })} data-testid="ret-valor-iva" className="font-semibold" />
              </div>
            </div>
          </div>

          <div>
            <Label>Observaciones</Label>
            <Textarea
              rows={2}
              value={form.observacion}
              onChange={e => setForm({ ...form, observacion: e.target.value })}
            />
          </div>

          <div className="bg-[#e9f2ee] rounded p-3 flex justify-between items-center border border-[#9ec4b1]">
            <span className="font-semibold text-[#15241d]">Total retención a registrar:</span>
            <span className="text-2xl font-bold text-[#205c45] tabular-nums">{fmt(totalRet)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-[#205c45] hover:bg-[#15241d] text-white" data-testid="ret-guardar">
            {saving ? "Guardando..." : "Registrar retención"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
